import React from 'react';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import emptyBox from 'images/empty-box.svg';
import {
  UnableToFindRepositories,
  LoadingTemplate,
  LimitExceeded,
  FullWidthCircularProgress,
  Box,
  Subtitle
} from './RepositoriesModal/repositories-modal.components';
import {
  findRelevantSensorAuthorization,
  shouldShowNoReposTemplate,
  inviteYourAdmin,
  talkToUs,
  findAndDeleteSensor
} from './RepositoriesModal/repositories-modal.service';
import RepositoriesModalErrorHandler from './RepositoriesModal/RepositoriesModalErrorHandler';
import RepositoriesModalHeader from './RepositoriesModal/RepositoriesModalHeader';
import useRepositoriesModal from './RepositoriesModal/useRepositoriesModal';
import RepositoriesModalContent from './RepositoriesModal/RepositoriesModalContent';
import RepositoriesModalFooter from './RepositoriesModal/RepositoriesModalFooter';
import { pluralize, isTeamByLicense } from 'common/utils/utils';
import { getProviderConfig } from 'common/utils/provider.utils';

const RepositoriesModal = props => {
  const {
    license,
    gitIntegrations,
    sensor_authorization_id,
    deleteSensorAuthorization,
    toggleSnackBar,
    account,
    openReAuthModal,
    isConnectGitStreamFlow,
    repositories,
    createRepositories,
    validateRepos,
    onSubmitCb,
    onRemoveMessage,
    backfillType,
    newRepositories
  } = props;

  const {
    sensor_name = '',
    base_url = '',
    is_pat: isPat
  } = findRelevantSensorAuthorization(
    gitIntegrations,
    sensor_authorization_id
  ) || {};

  const providerConfig = getProviderConfig({ provider: sensor_name });
  const azureOrganizationName = providerConfig.organizationFromBaseUrl
    ? providerConfig.organizationFromBaseUrl(base_url)
    : null;

  const {
    toggleRepositorySelection,
    isCheckboxDisabled,
    getNumOfSelectableRepos,
    onSubmit,
    submit,
    setTerm,
    setIsConfirmationDialogOpen,
    setOpenedOwnerRepositories,
    setSelected,
    ownerRepositories,
    loading,
    saving,
    error,
    isConfirmationDialogOpen,
    openedOwnerRepositories,
    numOfSelectableRepos,
    selected,
    stateRepositories,
    term,
    isOnboarding,
    multipleNew,
    activeRepos
  } = useRepositoriesModal({
    sensor_authorization_id,
    repositories,
    license,
    validateRepos,
    createRepositories,
    onSubmitCb,
    onRemoveMessage,
    backfillType,
    newRepositories,
    providerConfig,
    account,
    sensor_name,
    isPat
  });

  if (!sensor_name) {
    return null;
  }

  const showNoRepos = shouldShowNoReposTemplate(activeRepos, isOnboarding);

  const selectedReposMessage = `${pluralize(selected.size, 'repo')} selected`;

  const isTeamLicense = isTeamByLicense(license);

  const title = isConnectGitStreamFlow
    ? 'Connect the cm repo'
    : isOnboarding && !isTeamLicense
    ? 'Select Repositories'
    : 'Select repositories for your organization';

  if (!gitIntegrations.length) {
    return null;
  }

  if (error) {
    return (
      <RepositoriesModalErrorHandler
        error={error}
        gitIntegrations={gitIntegrations}
        sensor_authorization_id={sensor_authorization_id}
        deleteSensorAuthorization={deleteSensorAuthorization}
        isOnboarding={isOnboarding}
        openReAuthModal={openReAuthModal}
      />
    );
  }

  if (!!loading) {
    return (
      <Flex direction='column' height='100%'>
        <LoadingTemplate text='Discovering new repositories...' />
      </Flex>
    );
  }

  if (numOfSelectableRepos <= 0) {
    return (
      <Flex direction='column' height='100%'>
        <LimitExceeded maxRepos={license.maxRepos} />
      </Flex>
    );
  }

  if (showNoRepos) {
    return (
      <Flex
        direction='column'
        {...(isOnboarding ? {} : { justify: 'center', items: 'center' })}
        height='100%'
      >
        <UnableToFindRepositories
          provider={sensor_name}
          withTitleAndBoxes={isOnboarding}
          isOnboarding={isOnboarding}
          isTeamLicense={isTeamLicense}
          accessTokenCb={() =>
            findAndDeleteSensor({
              isOnboarding,
              gitIntegrations,
              sensor_authorization_id,
              deleteSensorAuthorization
            })
          }
          inviteYourAdminCb={() => inviteYourAdmin(toggleSnackBar, account)}
          talkToUsCb={talkToUs}
        />
      </Flex>
    );
  }

  return (
    <Flex direction='column' height='100%'>
      <ConfirmationDialog
        title='Hang on!'
        image={emptyBox}
        content={
          <Text color='shark' size='large' weight='400'>
            Looks like you selected repos with no recent activity. To see the
            magic of LinearB, pick a few repos with some recent action.
          </Text>
        }
        confirmText='Continue Anyway'
        cancelText='Add Repositories'
        isOpen={isConfirmationDialogOpen}
        onCancel={() => {
          TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
            page: TRACKING.PAGES.ONBOARDING,
            page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
            click_type: TRACKING.CLICK.CANCEL_EMPTY_REPOS
          });
          setIsConfirmationDialogOpen(false);
        }}
        onConfirm={() => {
          TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
            page: TRACKING.PAGES.ONBOARDING,
            page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
            click_type: TRACKING.CLICK.CONTINUE_EMPTY_REPOS
          });
          setIsConfirmationDialogOpen(false);
          submit();
        }}
      />
      <Flex direction='column' pl='medium' pr='medium' height='100%'>
        <RepositoriesModalHeader
          sensor_name={sensor_name}
          title={title}
          subtitle={
            <Subtitle
              isOnboarding={isOnboarding}
              license={license}
              isConnectGitStreamFlow={isConnectGitStreamFlow}
            />
          }
          setTerm={setTerm}
        />
        <Box>
          {stateRepositories.length ? (
            <RepositoriesModalContent
              providerConfig={providerConfig}
              toggleRepositorySelection={toggleRepositorySelection}
              isCheckboxDisabled={isCheckboxDisabled}
              getNumOfSelectableRepos={getNumOfSelectableRepos}
              setOpenedOwnerRepositories={setOpenedOwnerRepositories}
              setSelected={setSelected}
              ownerRepositories={ownerRepositories}
              openedOwnerRepositories={openedOwnerRepositories}
              selected={selected}
              stateRepositories={stateRepositories}
              term={term}
              isOnboarding={isOnboarding}
              multipleNew={multipleNew}
              azureOrganizationName={azureOrganizationName}
            />
          ) : (
            <FullWidthCircularProgress />
          )}
        </Box>
      </Flex>
      <RepositoriesModalFooter
        isOnboarding={isOnboarding}
        gitIntegrations={gitIntegrations}
        sensor_authorization_id={sensor_authorization_id}
        deleteSensorAuthorization={deleteSensorAuthorization}
        toggleSnackBar={toggleSnackBar}
        account={account}
        selectedReposMessage={selectedReposMessage}
        selected={selected}
        numOfSelectableRepos={numOfSelectableRepos}
        saving={saving}
        onSubmit={onSubmit}
        isTeamLicense={isTeamLicense}
        license={license}
        isConnectGitStreamFlow={isConnectGitStreamFlow}
      />
    </Flex>
  );
};

export default RepositoriesModal;
